import { Check, CheckCheck, ExternalLink, FileSignature, Paperclip, Receipt } from 'lucide-react'
import {
  formatMessageTime,
  getMessagingContextTheme,
  parseAttachmentMessage,
  parseDocumentReference,
  parseStructuredPayload,
} from './chat-utils'
import DocumentCard from './DocumentCard'

export default function ChatMessageBubble({
  message,
  currentUserId,
  linkedDocument,
  onOpenDocument,
  peerLastReadAt,
  messagingContext = 'particulier',
}) {
  const contextTheme = getMessagingContextTheme(messagingContext)
  if (message.type === 'SYSTEM') {
    const documentRef = parseDocumentReference(message.text)
    if (documentRef && linkedDocument) {
      return (
        <div className="flex justify-center my-4">
          <DocumentCard
            document={linkedDocument}
            onPress={() => onOpenDocument?.(documentRef.documentId)}
          />
        </div>
      )
    }
    return (
      <div className="flex justify-center my-4">
        <span className="text-xs text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
          {documentRef?.fallbackText ?? message.text}
        </span>
      </div>
    )
  }

  const isMine = message.sender?.id === currentUserId
  const readByPeer =
    isMine &&
    peerLastReadAt &&
    new Date(message.createdAt).getTime() <= new Date(peerLastReadAt).getTime()

  const renderContent = () => {
    const attachment = parseAttachmentMessage(message.text)
    if (attachment) {
      return (
        <a
          href={attachment.url}
          target="_blank"
          rel="noopener noreferrer"
          className={`flex items-center gap-3 rounded-xl border p-3 transition-colors ${
            isMine
              ? 'border-white/30 bg-white/10 hover:bg-white/15'
              : 'border-slate-200 bg-slate-50 hover:bg-slate-100'
          }`}
        >
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
            isMine ? 'bg-white/20 text-white' : 'bg-white text-slate-600 border border-slate-200'
          }`}>
            <Paperclip className="w-5 h-5" />
          </div>
          <div className="min-w-0 flex-1">
            <p className={`text-xs font-semibold uppercase tracking-wide ${isMine ? 'text-white/70' : 'text-slate-500'}`}>
              Pièce jointe
            </p>
            <p className={`font-medium truncate ${isMine ? 'text-white' : 'text-slate-900'}`}>
              {attachment.fileName}
            </p>
          </div>
          <ExternalLink className={`w-4 h-4 shrink-0 ${isMine ? 'text-white/80' : 'text-slate-400'}`} />
        </a>
      )
    }

    const structured = parseStructuredPayload(message)

    if (structured?.kind === 'contract') {
      const d = structured.data
      return (
        <div className="space-y-2">
          <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${
            isMine ? 'bg-white/20 text-white' : 'bg-[hsl(var(--primary)/0.08)] text-[hsl(var(--primary))]'
          }`}>
            <FileSignature className="w-3.5 h-3.5" />
            Contrat
          </div>
          <p className={`font-semibold ${isMine ? 'text-white' : 'text-slate-900'}`}>{d.title}</p>
          {d.description && (
            <p className={`text-sm ${isMine ? 'text-white/80' : 'text-slate-500'}`}>{d.description}</p>
          )}
          <div className={`border-t pt-2 space-y-1 ${isMine ? 'border-white/20' : 'border-slate-200'}`}>
            <div className="flex justify-between gap-2 text-sm">
              <span className={isMine ? 'text-white/70' : 'text-slate-500'}>Montant</span>
              <span className={isMine ? 'text-white font-medium' : 'text-slate-900'}>{d.amount}</span>
            </div>
            <div className="flex justify-between gap-2 text-sm">
              <span className={isMine ? 'text-white/70' : 'text-slate-500'}>Durée</span>
              <span className={isMine ? 'text-white font-medium' : 'text-slate-900'}>{d.duration}</span>
            </div>
          </div>
        </div>
      )
    }

    if (structured?.kind === 'quote') {
      const d = structured.data
      const validItems = (d.items ?? []).filter((item) => item.label?.trim() && item.amount?.toString().trim())
      return (
        <div className="space-y-2">
          <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${
            isMine ? 'bg-white/20 text-white' : 'bg-emerald-50 text-emerald-700'
          }`}>
            <Receipt className="w-3.5 h-3.5" />
            Devis
          </div>
          <p className={`font-semibold ${isMine ? 'text-white' : 'text-slate-900'}`}>{d.title}</p>
          {validItems.map((item, i) => (
            <div key={i} className="flex justify-between gap-2 text-sm">
              <span className={isMine ? 'text-white/90' : 'text-slate-700'}>{item.label}</span>
              <span className={isMine ? 'text-white font-medium' : 'text-slate-900'}>{item.amount}</span>
            </div>
          ))}
          <div className={`border-t pt-2 flex justify-between gap-2 ${isMine ? 'border-white/20' : 'border-slate-200'}`}>
            <span className={`text-sm ${isMine ? 'text-white/70' : 'text-slate-500'}`}>Total</span>
            <span className={`font-semibold ${isMine ? 'text-white' : 'text-emerald-600'}`}>{d.total}</span>
          </div>
          {d.validUntil?.trim() && (
            <p className={`text-xs ${isMine ? 'text-white/60' : 'text-slate-500'}`}>
              Valide jusqu&apos;au {d.validUntil}
            </p>
          )}
        </div>
      )
    }

    return (
      <p className={`whitespace-pre-wrap ${isMine ? 'text-white' : 'text-slate-900'}`}>
        {message.text}
      </p>
    )
  }

  return (
    <div className={`flex ${isMine ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className={`flex items-end gap-2 max-w-[75%] ${isMine ? 'flex-row-reverse' : ''}`}>
        {!isMine && (
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-slate-400 to-slate-500 flex items-center justify-center text-white text-sm font-medium shrink-0 overflow-hidden">
            {message.sender?.photoUrl ? (
              <img src={message.sender.photoUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              message.sender?.name?.charAt(0) || 'U'
            )}
          </div>
        )}
        <div
          className={`rounded-2xl px-4 py-2 shadow-sm ${
            isMine
              ? contextTheme.bubbleMineClass
              : 'bg-white border border-slate-200 rounded-bl-md'
          }`}
        >
          {renderContent()}
          <div className={`text-xs mt-1 flex items-center gap-1 ${isMine ? 'text-white/70' : 'text-slate-400'}`}>
            {formatMessageTime(message.createdAt)}
            {isMine && (
              readByPeer ? (
                <CheckCheck className="w-3.5 h-3.5 text-white/90" aria-label="Lu" />
              ) : (
                <Check className="w-3.5 h-3.5 text-white/50" aria-label="Envoyé" />
              )
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
